import type { paths } from "@/shared/types/api";

type ApiResponse<T> = {
	data?: T;
	error?: string;
};

/**
 * API Base URL
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Get ID token from localStorage
 */
function getIdToken(): string | null {
	if (typeof window === "undefined") return null;

	// Find the LastAuthUser key
	const keys = Object.keys(localStorage);
	const lastAuthUserKey = keys.find(
		(key) => key.includes("CognitoIdentityServiceProvider") && key.endsWith("LastAuthUser"),
	);

	if (!lastAuthUserKey) return null;

	const lastAuthUser = localStorage.getItem(lastAuthUserKey);
	if (!lastAuthUser) return null;

	// Construct the ID token key
	const userPoolClientId = lastAuthUserKey.split(".")[1];
	const idTokenKey = `CognitoIdentityServiceProvider.${userPoolClientId}.${lastAuthUser}.idToken`;

	return localStorage.getItem(idTokenKey);
}

/**
 * Generic API client function
 */
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
	try {
		const accessToken = getIdToken();
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};

		if (accessToken) {
			headers.Authorization = `Bearer ${accessToken}`;
		}

		// Merge with any additional headers from options
		if (options?.headers) {
			Object.entries(options.headers).forEach(([key, value]) => {
				if (typeof value === "string") {
					headers[key] = value;
				}
			});
		}

		const response = await fetch(`${API_BASE_URL}${endpoint}`, {
			...options,
			headers,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			return {
				error: errorData.error?.message || `HTTP error! status: ${response.status}`,
			};
		}

		const data = await response.json();
		return { data };
	} catch (error) {
		return {
			error: error instanceof Error ? error.message : "Unknown error occurred",
		};
	}
}

/**
 * Type-safe API client for the backend
 */
export const apiClient = {
	/**
	 * Health check
	 */
	healthcheck: {
		get: async () => {
			type Response =
				paths["/healthcheck"]["get"]["responses"]["200"]["content"]["application/json"];
			return fetchApi<Response>("/healthcheck");
		},
	},

	/**
	 * Information endpoints
	 */
	information: {
		list: async () => {
			type Response =
				paths["/information"]["get"]["responses"]["200"]["content"]["application/json"];
			return fetchApi<Response>("/information");
		},

		create: async (
			data: NonNullable<
				paths["/information"]["post"]["requestBody"]
			>["content"]["application/json"],
		) => {
			type Response =
				paths["/information"]["post"]["responses"]["201"]["content"]["application/json"];
			return fetchApi<Response>("/information", {
				method: "POST",
				body: JSON.stringify(data),
			});
		},

		get: async (id: number) => {
			type Response =
				paths["/information/{id}"]["get"]["responses"]["200"]["content"]["application/json"];
			return fetchApi<Response>(`/information/${id}`);
		},

		update: async (
			id: number,
			data: {
				title: string;
				body: string;
				filenameList: string[];
			},
		) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			return fetchApi<any>(`/information/${id}`, {
				method: "PUT",
				body: JSON.stringify(data),
			});
		},

		delete: async (id: number) => {
			type Response =
				paths["/information/{id}"]["delete"]["responses"]["200"]["content"]["application/json"];
			return fetchApi<Response>(`/information/${id}`, {
				method: "DELETE",
			});
		},
	},
};

/**
 * Export types for use in components
 */
export type Information =
	paths["/information"]["get"]["responses"]["200"]["content"]["application/json"]["informationList"][number];
export type CreateInformation = NonNullable<
	paths["/information"]["post"]["requestBody"]
>["content"]["application/json"];
export type UpdateInformation = NonNullable<
	paths["/information/{id}"]["put"]["requestBody"]
>["content"]["application/json"];
